import React, { useCallback, useRef, useState } from 'react';
import { Box } from '@mui/material';
import styles from '../../styles';
import Draw from '../../draw';
import useIsMobile from '../../../../../hooks/useIsMobile';
import { useGalleryContext } from '../..';
import { identitySelector } from '../../selectors';
import { Spinner } from 'components/core';
import {
  signedRequestService,
  thumbnailService,
} from 'services/fileService';
import { useBlobFileStorage } from 'components/core/BlobFileStorage';
import {
  drawingDisplayProbeImageSx,
  drawingDisplayRootSx,
} from './styles';
import { DrawingDisplayProps } from './types';

const DrawingDisplay: React.FC<DrawingDisplayProps> = (props) => {
  const { setDrawMode, drawing, addDrawing, idx } = props;
  const [file] = useGalleryContext().useMainDisplayedFile(identitySelector);
  const blobFileStorage = useBlobFileStorage();
  const isMobile = useIsMobile();
  const cs = styles({ isMobile });
  const ref = useRef<HTMLDivElement | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  // The probe image of which file and source loaded: drawing starts only on the current one.
  const [loadedProbeKey, setLoadedProbeKey] = useState<string>();

  const storedBlobUrl = file
    ? thumbnailService.resolveStoredBlobUrl(
      drawing,
      file,
      blobFileStorage.getItem
    )
    : undefined;

  const imageSrc = file
    ? signedRequestService.resolveDrawInitialSource(file, drawing, false) ||
      signedRequestService.resolveFullPreviewSource(
        storedBlobUrl,
        drawing,
        file
      )
    : undefined;

  const fileIdentity = file ? file._id || file.localId || file.title : undefined;
  const probeKey = `${fileIdentity},${imageSrc}`;
  const probeLoaded = loadedProbeKey === probeKey;

  const updateContainer = useCallback(() => {
    setContainer(ref.current);
    setLoadedProbeKey(probeKey);
  }, [probeKey]);

  if (!file) return null;

  if (!imageSrc) {
    return (
      <Box
        ref={ref}
        className={cs.mainFileDisplay}
        sx={drawingDisplayRootSx}
      >
        <Spinner reason='DrawingDisplay missing image source' />
      </Box>
    );
  }

  return (
    <Box ref={ref} className={cs.mainFileDisplay} sx={drawingDisplayRootSx}>
      {!probeLoaded && (
        <Spinner reason='DrawingDisplay probe image loading' />
      )}
      <Box
        component='img'
        key={probeKey}
        className={cs.drawingDisplay}
        src={imageSrc}
        crossOrigin='use-credentials'
        sx={drawingDisplayProbeImageSx}
        alt={file.description || file.title}
        onLoad={updateContainer}
      />
      {container && probeLoaded && (
        <Draw
          container={container}
          file={file}
          setDrawMode={setDrawMode}
          addDrawing={addDrawing}
          idx={idx}
          drawing={drawing}
        />
      )}
    </Box>
  );
};

export default DrawingDisplay;
